"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import type { AuthAdapter, AdapterOptions } from "../adapters/types";
import type { AuthProvider, AuthUser } from "../types";
import { sessionCore } from "../core/session-core";
import { parseJwtUser, parseApiTokens } from "../core/jwt-decode";
import { refreshWithDedup } from "../core/refresh";
import { authApi } from "../api";
import { mergeAdjustAdidIntoHeaders } from "@/src/lib/bridge/adjustRequestHeaders";
import { authActions } from "../store/auth-store";
import {
  TOKEN_REFRESH_INTERVAL_MS,
  TOKEN_REFRESH_THRESHOLD_MS,
} from "../constants";
import {
  coldStartRestore,
  persistLoginType,
  detectLoginType,
} from "./cold-start";
import { startLoginStoreBridge } from "../compat/login-store-bridge";
import { getEnvIndexStorageBaseUrl } from "../../api/osp/constants";
import { getBridge } from "@/src/lib/bridge/bridge";
import { clearBusinessStateOnLogout } from "@store/modules/logoutCleanup";
import { markJustLoggedOut } from "@features/login/authSessionSyncRefs";
import {
  isLoginPagePath,
  redirectToLoginPage,
} from "@features/login/redirectToLoginPage";
import {
  trackAuthSigninSuccess,
  trackObLoginSuccess,
} from "@hooks/onboarding/onboardingAnalytics";
import { resolveIntroAuthPlatform } from "@components/SignInIntro/resolveIntroAuthPlatform";
import { consumePendingRedirectProvider } from "../adapters/firebase-social";
import {
  AUTH_PROVIDER_RESULT,
  AUTH_SIGNIN_ERROR_STAGE,
  type AuthSigninErrorStage,
  isAuthCancelled,
  trackAuthProviderResult,
  trackAuthSigninError,
} from "../authTracking";
import { mergeDeviceHeaders } from "../deviceHeaders";

export interface LogoutOptions {
  /**
   * 登出后是否跳转登录页，默认 true。
   * 仅 extension 登出弹窗需要 false —— 它清完会话后要 `window.close()`，
   * 跳转会把弹窗带到登录页而不是关掉。
   */
  redirect?: boolean;
}

interface AuthContextValue {
  login: (adapter: AuthAdapter, options?: AdapterOptions) => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * GA `ob_login_success` 的 auth_method —— **沿用 web 历史口径**（GA 冻结）：
 * 旧调用是 `String(LoginStore.loginType)`，取值来自 `@osp/osp-web3-login` 的 `LoginType` 数字枚举，
 * 社交（Google / Apple / X）一律 `"1"`、邮箱 `"2"`。
 *
 * ⚠️ 原生发的是 `owner.issue_platform`（GOOGLE / APPLE / X / EMAIL），两端值域本来就不同，
 * 按 auth_method 合并看时要做映射；这里不改，改了就是单方面改 GA 存量口径。
 */
const OB_LOGIN_AUTH_METHOD_SOCIAL = "1";
const OB_LOGIN_AUTH_METHOD_EMAIL = "2";
const OB_LOGIN_AUTH_METHOD: Record<AuthProvider, string> = {
  google: OB_LOGIN_AUTH_METHOD_SOCIAL,
  apple: OB_LOGIN_AUTH_METHOD_SOCIAL,
  twitter: OB_LOGIN_AUTH_METHOD_SOCIAL,
  email: OB_LOGIN_AUTH_METHOD_EMAIL,
};

/** JWT 里没有真实 owner id 时的占位值（同原生 `awaitRealOwnerId` 的过滤口径） */
const JWT_PLACEHOLDER_UID = "unknown";

/**
 * 登录成功（`/auth/login` 已返回且会话已写入）。
 *
 * `ob_login_success` 只在这里发 —— 弹窗 / 登录页 / redirect 回跳三条路径都收口到 orchestrator。
 * 它原本在旧 `LoginModalProvider.handleLoginSuccess` 里，2026-07-20 换新 auth 模块的合并
 * （087739f56b）把调用丢了，此后 web 一条都没有。
 */
function reportLoginSuccess(user: AuthUser): void {
  // App 内 WebView 登录由原生负责发这条 GA（UserPreferencesRepository，按账号持久化去重），web 再发就是双发
  if (isInNativeApp()) {
    return;
  }
  trackObLoginSuccess({
    auth_method: OB_LOGIN_AUTH_METHOD[user.loginType],
    // 历史口径：取不到 owner id 时传空串（旧调用 `UserStore.ownerInfo?.id ?? ""`）
    user_id: user.uid && user.uid !== JWT_PLACEHOLDER_UID ? user.uid : "",
  });
}

function isInNativeApp(): boolean {
  try {
    return getBridge().isInApp();
  } catch {
    return false;
  }
}

/** 失败只在 orchestrator 报一次；取消不算失败，只记 provider 段的 cancel。 */
function reportLoginFailure(
  provider: AuthProvider,
  stage: AuthSigninErrorStage,
  error: unknown,
): void {
  const cancelled = isAuthCancelled(error);
  if (stage === AUTH_SIGNIN_ERROR_STAGE.PROVIDER && provider !== "email") {
    trackAuthProviderResult(
      provider,
      cancelled ? AUTH_PROVIDER_RESULT.CANCEL : AUTH_PROVIDER_RESULT.FAIL,
      error,
    );
  }
  if (!cancelled) {
    trackAuthSigninError(provider, stage, error);
  }
}

/**
 * 清除 Firebase Web 端会话。
 *
 * 不清会导致下次三方登录静默复用同一身份，用户无法切换账号。
 * 邮箱 OTP 登录不初始化 Firebase，`getAuth()` 会抛错 —— 必须独立兜住，
 * 否则异常会中断整个登出流程，退化成"点登出没反应"。
 */
async function signOutFirebaseWeb(): Promise<void> {
  try {
    const { getAuth, signOut } = await import("firebase/auth");
    await signOut(getAuth());
  } catch {
    // Firebase 未初始化（邮箱登录）或已登出：忽略
  }
}

export function AuthOrchestrator({ children }: { children: ReactNode }) {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    coldStartRestore();
    checkRedirectResult();
    startRefreshTimer();
    const stopLoginStoreBridge = startLoginStoreBridge();

    const stopStorageSync = setupStorageSync();
    const stopBridgeSync = setupBridgeSync();

    return () => {
      stopRefreshTimer();
      stopLoginStoreBridge();
      stopStorageSync();
      stopBridgeSync();
    };
  }, []);

  /**
   * PWA standalone 下社交登录走 `signInWithRedirect`，页面跳走再回来，结果在这里接住。
   *
   * 只有本 tab 真的发起过 redirect（`consumePendingRedirectProvider` 有值）才上报 —— 邮箱用户
   * 冷启动时 Firebase 未初始化、`getAuth()` 会抛，那是常态不是登录失败。
   */
  async function checkRedirectResult() {
    const pendingProvider = consumePendingRedirectProvider();
    let result: import("firebase/auth").UserCredential | null;
    try {
      const { getAuth, getRedirectResult } = await import("firebase/auth");
      result = await getRedirectResult(getAuth());
    } catch (error) {
      if (pendingProvider) {
        reportLoginFailure(
          pendingProvider,
          AUTH_SIGNIN_ERROR_STAGE.PROVIDER,
          error,
        );
      }
      return;
    }
    if (!result) {
      // 本 tab 发起过 redirect、回来却没有结果 = 用户在授权页放弃返回（取消）；没发起过则是普通加载，不报
      if (pendingProvider) {
        trackAuthProviderResult(pendingProvider, AUTH_PROVIDER_RESULT.CANCEL);
      }
      return;
    }
    const provider =
      pendingProvider ??
      ((result.providerId?.replace(".com", "") as AuthProvider) || "google");
    trackAuthProviderResult(provider, AUTH_PROVIDER_RESULT.SUCCESS);
    try {
      const idToken = await result.user.getIdToken();
      const apiResult = await authApi.login(
        mergeDeviceHeaders(
          mergeAdjustAdidIntoHeaders({ "OS-Firebase-Token": idToken }),
        ),
      );
      const tokens = parseApiTokens(apiResult);
      sessionCore.setTokens(tokens);
      const user = parseJwtUser(tokens.accessToken, provider);
      persistLoginType(provider);
      authActions.setAuthenticated(user, tokens);
      reportLoginSuccess(user);
      // 独立登录页由 `screens/LoginPage` 的 isLoggedIn effect 统一报 auth_signin_success，
      // 这里只补「从弹窗发起、回跳到原页面」这条，两处都发会把同一次成功记两遍
      if (!isLoginPagePath(window.location.pathname)) {
        trackAuthSigninSuccess({
          platform: resolveIntroAuthPlatform(),
          method: provider,
          entry: "login_modal",
        });
      }
    } catch (error) {
      reportLoginFailure(provider, AUTH_SIGNIN_ERROR_STAGE.API, error);
    }
  }

  function startRefreshTimer() {
    timerRef.current = setInterval(() => {
      if (
        sessionCore.isExpiringSoon(TOKEN_REFRESH_THRESHOLD_MS) &&
        sessionCore.canRefresh()
      ) {
        refreshWithDedup().then((refreshed) => {
          if (refreshed) {
            authActions.updateTokens(refreshed);
          } else {
            sessionCore.clear();
            authActions.reset();
          }
        });
      }
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  function stopRefreshTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function setupStorageSync(): () => void {
    if (typeof window === "undefined") {
      return () => {};
    }

    const credentialsKey = `osp.${getEnvIndexStorageBaseUrl(process.env.NEXT_PUBLIC_ENV ?? "dev")}.credentials`;
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== credentialsKey) {
        return;
      }
      if (event.newValue) {
        const tokens = sessionCore.getTokens();
        if (tokens) {
          try {
            const user = parseJwtUser(tokens.accessToken, detectLoginType());
            authActions.setAuthenticated(user, tokens);
          } catch {
            /* parse failed, ignore */
          }
        }
      } else {
        authActions.reset();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }

  function setupBridgeSync(): () => void {
    if (typeof window === "undefined") {
      return () => {};
    }

    const handleBridgeMessage = async (event: MessageEvent) => {
      const data = event.data;

      if (data?.type === "auth-session-updated") {
        const tokens = sessionCore.getTokens();
        if (tokens) {
          try {
            const user = parseJwtUser(tokens.accessToken, detectLoginType());
            authActions.setAuthenticated(user, tokens);
          } catch {
            /* parse failed, ignore */
          }
        }
      }
    };
    window.addEventListener("message", handleBridgeMessage);
    return () => window.removeEventListener("message", handleBridgeMessage);
  }

  const login = async (adapter: AuthAdapter, options?: AdapterOptions) => {
    authActions.startAuth(adapter.provider);
    const isSocial = adapter.provider !== "email";
    let stage: AuthSigninErrorStage = AUTH_SIGNIN_ERROR_STAGE.PROVIDER;
    try {
      const adapterResult = await adapter.authenticate(options);
      if (isSocial) {
        trackAuthProviderResult(adapter.provider, AUTH_PROVIDER_RESULT.SUCCESS);
      }
      stage = isSocial
        ? AUTH_SIGNIN_ERROR_STAGE.API
        : AUTH_SIGNIN_ERROR_STAGE.VERIFY;
      const apiResult = await authApi.login(
        mergeDeviceHeaders(
          mergeAdjustAdidIntoHeaders(adapterResult.authHeaders),
        ),
      );
      const tokens = parseApiTokens(apiResult);
      sessionCore.setTokens(tokens);
      const user = parseJwtUser(tokens.accessToken, adapter.provider);
      persistLoginType(adapter.provider);
      authActions.setAuthenticated(user, tokens);
      reportLoginSuccess(user);
      try {
        const bridge = getBridge();
        if (bridge.isInApp()) {
          bridge.broadcastAuthSessionUpdated({
            userId: user.uid,
          });
        }
      } catch {
        // non-app: ignore
      }
    } catch (error) {
      authActions.setError({
        type: "provider_error",
        provider: adapter.provider,
        code: (error as Error).name ?? "UNKNOWN",
        message: (error as Error).message ?? "Login failed",
      });
      reportLoginFailure(adapter.provider, stage, error);
      throw error;
    } finally {
      adapter.destroy?.();
    }
  };

  /**
   * 统一登出入口 —— 所有登出 UI 必须走这里。
   *
   * 顺序有约束：token / 业务缓存必须全部清完再通知原生 reload 各 Tab，
   * 否则被 reload 的 WebView 会读到尚未清除的旧登录态。
   */
  const logout = async (options?: LogoutOptions) => {
    stopRefreshTimer();
    sessionCore.clear();
    authActions.reset();
    clearBusinessStateOnLogout();
    await signOutFirebaseWeb();
    // 须在 localStorage / 业务缓存全部清完后写标记，熔断"刚登出又被缓存恢复"
    markJustLoggedOut();
    try {
      const bridge = getBridge();
      if (bridge.isInApp()) {
        bridge.nativeFirebaseLogout();
        bridge.closeDetailInNative();
        bridge.syncShellTabsAfterLogout();
      }
    } catch {
      // non-app or old shell: ignore
    }
    if (options?.redirect !== false) {
      redirectToLoginPage();
    }
  };

  return (
    <AuthContext.Provider value={{ login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within AuthOrchestrator");
  }
  return ctx;
}
